/**
 * Centralized API Service for RBAC Frontend
 */

import { baseUrl } from '../config/environment';

// Create fetch wrapper with default configuration
const apiClient = {
  baseURL: baseUrl,
  
  // Generic request method
  async request(endpoint, options = {}) {
    // Ensure proper URL construction with forward slash
    const baseURL = this.baseURL.endsWith('/') ? this.baseURL : `${this.baseURL}/`;
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    const url = `${baseURL}${cleanEndpoint}`;
    const config = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, config);
      
      // Handle 401 Unauthorized
      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('uid');
        localStorage.removeItem('email');
        window.location.href = '/login';
        throw new Error('Session expired. Please log in again.');
      }

      // Handle other error statuses
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return response;
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  },

  // Convenience methods
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    let res = await this.request(url, { method: 'GET' });
    let response = await res.json();
    return response;
  },

  async post(endpoint, data = {}) {
    console.log('apiService.post - endpoint:', endpoint);
    console.log('apiService.post - data:', data);
    console.log('apiService.post - JSON.stringify(data):', JSON.stringify(data));
    
    let res = await this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    let response = await res.json();
    return response;
  },

  async put(endpoint, data = {}) {
    let res = await this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    let response = await res.json();
    return response;
  },

  async delete(endpoint, data = {}) {
    let res = await this.request(endpoint, {
      method: 'DELETE',
      body: Object.keys(data).length > 0 ? JSON.stringify(data) : undefined
    });
    let response = await res.json();
    return response;
  }
};

// API service methods
export const apiService = {
  // Generic methods
  get: (endpoint, params = {}) => apiClient.get(endpoint, params),
  post: (endpoint, data = {}) => apiClient.post(endpoint, data),
  put: (endpoint, data = {}) => apiClient.put(endpoint, data),
  delete: (endpoint) => apiClient.delete(endpoint),
  request: (endpoint, options = {}) => apiClient.request(endpoint, options),

  // Authentication
  login: (credentials) => apiClient.post('/login', credentials),
  logout: () => apiClient.post('/logout'),
  verifyToken: () => apiClient.get('/verifyToken'),
  verifySession: () => apiClient.get('/verifySession'),
  getCurrentUser: () => apiClient.get('/getCurrentUser'),

  // User Management
  AuctionCloseAlert: (userData) => apiClient.post('/auctionCloseAlert', userData),
  InspectionReminder: (userData) => apiClient.post('/inspectionReminder', userData),
  GatePassReminder: (userData) => apiClient.post('/gatePassReminder', userData),
  SendOTPDealer:(dealerData) => apiClient.post('/sendOTPDealer', dealerData),
  LoginDealer: (dealerData) => apiClient.post('/loginDealer', dealerData),
  createUser: (userData) => apiClient.post('/createUser', userData),
  RegisterDealer: (dealerData) => apiClient.post('/registerDealer', dealerData),
  createDealer: (dealerData) => apiClient.post('/createDealer', dealerData),
  uploadDocuments: (formData) => apiClient.post('/uploadDocuments', formData),
  createDealerInfo: (dealerData) => apiClient.post('/createDealerInfo', dealerData),
  DealerContract: (dealerData) => apiClient.post('/dealerContract', dealerData),
  getUsers: (params) => apiClient.get('/getUsers', params),
  getDealer: (params) => apiClient.get('/getDealerList', params),
  updateUser: (userData) => apiClient.put('/updateUser', userData),
  deleteUser: (userId) => apiClient.delete(`/deleteUser?userId=${userId}`),

  // Organization Management
  getOrganizations: (params) => apiClient.get('/getOrganizations', params),
  createOrganization: (orgData) => apiClient.post('/createOrganization', orgData),
  updateOrganization: (orgData) => apiClient.put('/updateOrganization', orgData),

  // Car Management
  createCar: (carData) => apiClient.post('/createCar', carData),
  updateCar: (carData) => apiClient.put('/updateCar', carData),
  getCars: (params) => apiClient.get('/getCars', params),
  deleteCar: (carId) => apiClient.delete(`/deleteCar?carId=${carId}`),

  // Dealer Functions
  getDealerHomeData: (orgId, dealerId) => apiClient.get('/getDealerHomeData', { 
    orgId: `${orgId}&dealerId=${dealerId}` 
  }),
  getDealerCarsByCity: (orgId, dealerId, city) => apiClient.get('/getDealerCarsByCity', { 
    orgId: `${orgId}&dealerId=${dealerId}`,
    city 
  }),
  addToMyCars: (data) => apiClient.post('/addToMyCars', data),

  // BDM Functions
  getBDMDataOptimized: (params) => apiClient.get('/getBDMDataOptimized', params),
  getBDMDashboardMetrics: (params) => apiClient.get('/getBDMDashboardMetricsOptimized', params),

  // Generic CRUD
  getAllDocuments: (params) => apiClient.get('/getAllDocuments', params),
  getDocumentById: (params) => apiClient.get('/getDocumentById', params),
  createDocument: (data) => apiClient.post('/createDocument', data),
  updateDocument: (data) => apiClient.put('/updateDocument', data),
  deleteDocument: (data) => apiClient.delete('/deleteDocument', data),

  // Action Logs
  addActionLog: (logData) => apiClient.post('/addActionLog', logData),

  // Role Management
  getRoles: () => apiClient.get('/getRolesList'),
  updateRole: (data) => {
    console.log('🔍 apiService updateRole - Sending to backend:', data);
    return apiClient.put('/updateRole', data);
  },
  createRole: (data) => apiClient.post('/createRole', data),
  deleteRole: (data) => apiClient.delete('/deleteRole', data),

  // Steering Wheel Management
  getSteeringWheels: () => apiClient.get('/getSteeringWheels'),
  createSteeringWheel: (data) => apiClient.post('/createSteeringWheel', data),
  updateSteeringWheel: (data) => apiClient.put('/updateSteeringWheel', data),
  deleteSteeringWheel: (data) => apiClient.delete('/deleteSteeringWheel', data),
  serveSteeringWheelImage: (params) => apiClient.get('/serveSteeringWheelImage', params),
  uploadSteeringWheelImage: (data) => apiClient.post('/uploadSteeringWheelImage', data),
  deleteSteeringWheelImage: (data) => apiClient.delete('/deleteSteeringWheelImage', data),

  // Vehicle Specification Management
  getVehicleSpecs: (params) => apiClient.get('/getVehicleSpecs', params),
  createVehicleSpec: (data) => apiClient.post('/createVehicleSpec', data),
  updateVehicleSpec: (data) => apiClient.put('/updateVehicleSpec', data),
  deleteVehicleSpec: (data) => apiClient.delete('/deleteVehicleSpec', data),
  getVehicleSpecById: (params) => apiClient.get('/getVehicleSpecById', params),

  // Yard Management
  getYards: (params) => apiClient.get('/getYards', params),
  submitYard: (data) => apiClient.post('/submitYard', data),
  updateYard: (data) => apiClient.put('/updateYard', data),
  deleteYard: (yardId) => apiClient.delete('/deleteYard', { yardId }),
  importYards: (data) => apiClient.post('/importYards', data),
  exportYardData: () => apiClient.get('/exportYardData'),
  importYardData: (data) => apiClient.post('/importYardData', data),

  // Cities Management
  getCitiesList: (params) => apiClient.get('/getCitiesList', params),

  // Type & Angle Data
  getTypeAngleData: (params) => apiClient.get('/getTypeAngleData', params),
  createTypeAngleData: (data) => apiClient.post('/createTypeAngleData', data),
  updateTypeAngleData: (id, data) => apiClient.put('/updateTypeAngleData', { id, ...data }),
  deleteTypeAngleData: (id) => apiClient.delete(`/deleteTypeAngleData?id=${id}`),

  // Overlay Management
  getOverlayMasters: (params) => apiClient.get('/getOverlayMasters', params),
  createOverlay: (data) => apiClient.post('/createOverlay', data),
  updateOverlay: (data) => apiClient.put('/updateOverlay', data),
  deleteOverlay: (data) => apiClient.delete('/deleteOverlay', data),
  uploadOverlayImage: (data) => apiClient.post('/uploadOverlayImage', data), // Deprecated - use uploadImage instead
  uploadImage: (data) => apiClient.post('/uploadImage', data),
  deleteOverlayImage: async (params) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/deleteOverlayImage?${queryString}` : '/deleteOverlayImage';
    const response = await apiClient.request(endpoint, { method: 'DELETE' });
    return response.json();
  },
};

export default apiService;
